import { memo } from 'react';

import { useT } from '@/app/i18n/client';
import type { IAccommodationOptionType } from '@/entities/accommodation/types';
import { useCurrency } from '@/features/product/contexts/CurrencyContext';
import { cn, utilFromMinorUnit } from '@/lib/utils';

import AccommodationAdditionalGuestOptionWarning from './AdditionalGuestOptionWarning';

interface IProps {
  roomOptionTypeList?: IAccommodationOptionType[];
  selectedOption: IAccommodationOptionType | null;
  handleOptionSelection: (
    option: IAccommodationOptionType,
  ) => (e: React.MouseEvent<HTMLButtonElement>) => void;
}
function AccommodationOptions({
  roomOptionTypeList = [],
  selectedOption,
  handleOptionSelection,
}: IProps) {
  const { t } = useT('product');

  const currency = useCurrency();

  if (roomOptionTypeList.length === 0) return null;

  return (
    <div className='mb-12'>
      <h3 className='mb-6 text-xl font-bold text-[#2E2E2E] max-sm:text-lg'>{t('options')}</h3>
      <div className='space-y-2'>
        {roomOptionTypeList.map(optionType => (
          <button
            key={optionType.idx}
            onClick={optionType.isActive ? handleOptionSelection(optionType) : undefined}
            className={cn(
              'flex w-full items-center justify-between gap-4 rounded-2xl border p-4 text-left font-medium transition-all duration-300 max-sm:flex-col max-sm:items-start sm:p-5 sm:text-lg',
              selectedOption?.idx === optionType.idx
                ? 'border-[#0064FF] bg-[#0064FF] text-white'
                : 'border-[#E9ECEF] bg-white text-[#2E2E2E] hover:border-[#0064FF]',
              optionType.isActive ? 'opacity-100' : 'cursor-not-allowed opacity-50',
            )}
          >
            <span className='max-w-[59%] break-all text-base max-sm:max-w-full'>
              {optionType.title} {optionType?.description ? `(${optionType.description})` : ''}
            </span>
            <div className='space-x-2 text-right max-sm:ml-auto'>
              <span className='text-sm'>{optionType.type.toLowerCase().replace('_', ' ')} </span>
              <span
                className={cn(
                  'text-lg font-bold',
                  selectedOption?.idx === optionType.idx && 'text-white',
                )}
              >
                {currency} {utilFromMinorUnit(optionType.price, currency)}
              </span>
            </div>
          </button>
        ))}
      </div>

      <AccommodationAdditionalGuestOptionWarning selectedOption={selectedOption} />
    </div>
  );
}

export default memo(AccommodationOptions);
